// ============================================================================
// CHANGO V5.0 — Reset Password Page (T-D1-023)
// ============================================================================
// Reads `token` from URL search params (link emailed to the user). Accepts a
// new password (same strength rules as Register), confirms it, POSTs to
//   /api/auth/reset-password { token, new_password }
// On success: navigate to /login with a success notice. The backend revokes
// all existing refresh tokens for the user, so every device has to re-login.
// ============================================================================

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { api, HttpError } from '../../api/client.js';

const schema = z.object({
  new_password: z.string()
    .min(8, '至少 8 位')
    .regex(/[a-z]/, '需包含小写字母')
    .regex(/[A-Z]/, '需包含大写字母')
    .regex(/\d/, '需包含数字'),
  confirm: z.string()
}).refine((v) => v.new_password === v.confirm, {
  message: '两次输入的密码不一致',
  path: ['confirm']
});

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: { new_password: '', confirm: '' }
  });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="card w-full max-w-md p-8 text-center">
          <h1 className="text-xl font-semibold text-slate-800">无效的重置链接</h1>
          <p className="mt-4 text-sm text-slate-600">
            链接中缺少必要参数。请检查邮件中的完整链接，或重新申请。
          </p>
          <div className="mt-6 flex justify-center gap-4 text-sm">
            <Link to="/forgot-password" className="text-chango-600 hover:underline">重新申请</Link>
            <span className="text-slate-400">|</span>
            <Link to="/login" className="text-slate-500 hover:text-slate-700">返回登录</Link>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async ({ new_password }) => {
    setServerError(null);
    setSubmitting(true);
    try {
      await api.post('/api/auth/reset-password', { token, new_password });
      navigate('/login', {
        replace: true,
        state: { notice: '密码已重置，请使用新密码登录。' }
      });
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.code === 'TOKEN_EXPIRED' || err.code === 'TOKEN_INVALID') {
          setServerError('链接已过期或无效，请重新申请。');
        } else {
          setServerError(err.message || '重置失败');
        }
      } else {
        setServerError('网络错误，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="card w-full max-w-md p-8">
        <h1 className="text-xl font-semibold text-slate-800">设置新密码</h1>

        {serverError ? <div role="alert" className="alert-error mt-4">{serverError}</div> : null}

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <div>
            <label className="label" htmlFor="new_password">新密码</label>
            <input
              id="new_password"
              type="password"
              autoComplete="new-password"
              className={`field ${errors.new_password ? 'field-error' : ''}`}
              {...register('new_password')}
            />
            {errors.new_password
              ? <p className="err">{errors.new_password.message}</p>
              : <p className="hint">至少 8 位，含大小写字母和数字。</p>}
          </div>

          <div>
            <label className="label" htmlFor="confirm">确认新密码</label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              className={`field ${errors.confirm ? 'field-error' : ''}`}
              {...register('confirm')}
            />
            {errors.confirm ? <p className="err">{errors.confirm.message}</p> : null}
          </div>

          <button type="submit" disabled={submitting || !isValid} className="btn-primary w-full">
            {submitting ? <span className="spinner" aria-hidden="true" /> : null}
            {submitting ? '提交中…' : '设置新密码'}
          </button>

          <p className="text-center text-sm">
            <Link to="/login" className="text-chango-600 hover:underline">返回登录</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
